/**
 * IMA SDK Loader - Singleton for loading Google IMA SDK
 * Ensures IMA SDK is loaded only once across all video ad instances
 * Includes timeout protection and retry capability
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { withRetry, withTimeout } from '../utils';
// Configuration
const IMA_LOAD_TIMEOUT_MS = 15000; // 15 seconds to load IMA
const IMA_RETRY_ATTEMPTS = 2; // Retry once on failure
export class IMALoader {
    /**
     * Load IMA SDK script with retry and timeout
     * @returns Promise that resolves when SDK is loaded
     */
    static load() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            // Already loaded
            if (this.loaded && ((_a = window.google) === null || _a === void 0 ? void 0 : _a.ima)) {
                return;
            }
            // Currently loading - return existing promise
            if (this.loading && this.loadPromise) {
                return this.loadPromise;
            }
            // If previous load failed, allow retry
            if (this.loadFailed) {
                this.loadFailed = false;
                this.loadPromise = null;
            }
            // Start loading with retry
            this.loading = true;
            this.loadPromise = withRetry(() => this.loadInternal(), {
                maxAttempts: IMA_RETRY_ATTEMPTS,
                initialDelayMs: 2000,
                onRetry: (attempt, error) => {
                    console.warn(`[GetJarAds] IMA SDK load attempt ${attempt} failed, retrying...`, error.message);
                },
            }).catch((error) => {
                this.loading = false;
                this.loadFailed = true;
                throw error;
            });
            return this.loadPromise;
        });
    }
    /**
     * Internal load logic with timeout
     */
    static loadInternal() {
        return __awaiter(this, void 0, void 0, function* () {
            return withTimeout(() => this.loadScript(), IMA_LOAD_TIMEOUT_MS, `IMA SDK script load timed out after ${IMA_LOAD_TIMEOUT_MS}ms`);
        });
    }
    /**
     * Core script loading logic
     */
    static loadScript() {
        return new Promise((resolve, reject) => {
            var _a;
            try {
                // Check if IMA SDK is already loaded (by another script)
                if ((_a = window.google) === null || _a === void 0 ? void 0 : _a.ima) {
                    console.log('[GetJarAds] IMA SDK already loaded');
                    this.loaded = true;
                    this.loading = false;
                    resolve();
                    return;
                }
                // Load IMA SDK script
                const script = document.createElement('script');
                script.src = this.IMA_SDK_URL;
                script.async = true;
                script.onload = () => {
                    var _a;
                    if (!((_a = window.google) === null || _a === void 0 ? void 0 : _a.ima)) {
                        this.loading = false;
                        reject(new Error('IMA SDK loaded but google.ima not available'));
                        return;
                    }
                    this.loaded = true;
                    this.loading = false;
                    console.log(`[GetJarAds] IMA SDK ${window.google.ima.VERSION} loaded successfully`);
                    resolve();
                };
                script.onerror = () => {
                    this.loading = false;
                    reject(new Error('Failed to load Google IMA SDK'));
                };
                document.head.appendChild(script);
            }
            catch (error) {
                this.loading = false;
                reject(error);
            }
        });
    }
    /**
     * Check if IMA SDK is loaded
     */
    static isLoaded() {
        var _a;
        return this.loaded && !!((_a = window.google) === null || _a === void 0 ? void 0 : _a.ima);
    }
    /**
     * Get IMA SDK instance
     * @throws Error if SDK not loaded
     */
    static getSDK() {
        var _a;
        if (!this.isLoaded() || !((_a = window.google) === null || _a === void 0 ? void 0 : _a.ima)) {
            throw new Error('IMA SDK not loaded. Call IMALoader.load() first.');
        }
        return window.google.ima;
    }
    /**
     * Reset loader state (for testing)
     */
    static reset() {
        this.loaded = false;
        this.loading = false;
        this.loadPromise = null;
    }
}
IMALoader.loaded = false;
IMALoader.loading = false;
IMALoader.loadPromise = null;
IMALoader.loadFailed = false;
// IMA SDK URL
IMALoader.IMA_SDK_URL = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
